/**
 * ImpersonationSelector — Sélection d'un profil à simuler (Admin uniquement)
 */
import { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { Loader2, Search, Eye } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ROLE_LABEL = { vendor: 'Vendeur', manager: 'Manager', responsable_agence: 'Resp. agence', partner: 'Partenaire', admin: 'Admin' };
const ROLE_COLOR = {
  vendor:  'bg-sky-100 text-sky-700 dark:bg-sky-900/30 dark:text-sky-300',
  manager: 'bg-violet-100 text-violet-700 dark:bg-violet-900/30 dark:text-violet-300',
  responsable_agence: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300',
  partner: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300',
  admin:   'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300',
};

const ROLE_ORDER = ['vendor', 'manager', 'responsable_agence', 'partner', 'admin'];

export function ImpersonationSelector({ open, onOpenChange }) {
  const { token, user: adminUser, startImpersonation } = useAuth();
  const [users, setUsers] = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [search, setSearch] = useState('');
  const [starting, setStarting] = useState(null); // email being started

  useEffect(() => {
    if (!open) return;
    setLoadingUsers(true);
    axios.get(`${API_URL}/users`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => setUsers(r.data || []))
      .catch(() => toast.error('Impossible de charger la liste des utilisateurs'))
      .finally(() => setLoadingUsers(false));
  }, [open, token]);

  const filtered = useMemo(() => {
    const q = search.toLowerCase();
    return users
      .filter(u => u.email !== adminUser?.email) // exclude self
      .filter(u =>
        !q ||
        u.email?.toLowerCase().includes(q) ||
        u.vendor_name?.toLowerCase().includes(q) ||
        u.role?.toLowerCase().includes(q)
      )
      .sort((a, b) => {
        const ra = ROLE_ORDER.indexOf(a.role);
        const rb = ROLE_ORDER.indexOf(b.role);
        if (ra !== rb) return ra - rb;
        return (a.vendor_name || a.email || '').localeCompare(b.vendor_name || b.email || '');
      });
  }, [users, search, adminUser]);

  // Group by role
  const grouped = useMemo(() => {
    const groups = {};
    for (const u of filtered) {
      if (!groups[u.role]) groups[u.role] = [];
      groups[u.role].push(u);
    }
    return groups;
  }, [filtered]);

  const handleSelect = async (targetEmail) => {
    setStarting(targetEmail);
    try {
      await startImpersonation(targetEmail);
      toast.success(`Vue simulée : ${targetEmail}`);
      onOpenChange(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur lors de la simulation');
    } finally {
      setStarting(null);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[80vh] flex flex-col gap-0 p-0" data-testid="impersonation-modal">
        <DialogHeader className="px-5 pt-5 pb-3 shrink-0">
          <DialogTitle className="text-base font-semibold flex items-center gap-2">
            <Eye className="size-4 text-violet-500" />
            Simuler une vue utilisateur
          </DialogTitle>
          <p className="text-xs text-muted-foreground mt-1">
            Sélectionnez un profil pour voir le CRM exactement comme cet utilisateur le voit.
          </p>
        </DialogHeader>

        <div className="px-5 pb-3 shrink-0">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 size-3.5 text-muted-foreground" />
            <Input
              autoFocus
              placeholder="Rechercher par nom, email ou rôle..."
              value={search}
              onChange={e => setSearch(e.target.value)}
              className="pl-8 h-9 text-sm"
              data-testid="impersonation-search"
            />
          </div>
        </div>

        <div className="flex-1 overflow-y-auto px-3 pb-4 space-y-4">
          {loadingUsers ? (
            <div className="flex items-center justify-center h-32">
              <Loader2 className="size-6 animate-spin text-muted-foreground" />
            </div>
          ) : filtered.length === 0 ? (
            <div className="flex items-center justify-center h-32 text-sm text-muted-foreground">
              Aucun utilisateur trouvé
            </div>
          ) : (
            ROLE_ORDER.filter(r => grouped[r]?.length > 0).map(role => (
              <div key={role}>
                <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider px-2 mb-1.5">
                  {ROLE_LABEL[role] || role} ({grouped[role].length})
                </p>
                <div className="space-y-1">
                  {grouped[role].map(u => (
                    <button
                      key={u.email}
                      onClick={() => handleSelect(u.email)}
                      disabled={!!starting}
                      data-testid={`impersonate-${u.email}`}
                      className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg hover:bg-secondary/60 transition-colors text-left group disabled:opacity-60"
                    >
                      <Avatar className="size-8 shrink-0">
                        <AvatarFallback className="text-xs bg-secondary">
                          {(u.vendor_name || u.email || '?').charAt(0).toUpperCase()}
                        </AvatarFallback>
                      </Avatar>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium truncate">
                          {u.vendor_name || u.email}
                        </p>
                        {u.vendor_name && (
                          <p className="text-xs text-muted-foreground truncate">{u.email}</p>
                        )}
                      </div>
                      <div className="flex items-center gap-2 shrink-0">
                        <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${ROLE_COLOR[u.role] || 'bg-secondary text-foreground'}`}>
                          {ROLE_LABEL[u.role] || u.role}
                        </span>
                        {starting === u.email ? (
                          <Loader2 className="size-3.5 animate-spin text-violet-500" />
                        ) : (
                          <Eye className="size-3.5 text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity" />
                        )}
                      </div>
                    </button>
                  ))}
                </div>
              </div>
            ))
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
